import { clsx } from "clsx";
import { Text } from "../../../../components/common/typography";

export interface VariantLite {
  id: number;
  sku: string | null;
  displayName: string;
  // The seller's current list price (sellerPrice on ProductVariantDto). Used
  // to render "Current" cell when diffing variant rows.
  currentPrice?: number | null;
  currentStock?: number | null;
  currentDiscount?: number | null;
}

export interface ProductContext {
  templateName?: string;
  variantsById: Map<number, VariantLite>;
  countryNameById?: Map<number, string>;
  categoryNameById?: Map<number, string>;
  /** Attribute value id → display name (e.g. 1 TB, Graphite). Used to render
   *  `newVariants` axis combos by name instead of raw `#id`. */
  attributeValueNameById?: Map<number, string>;
  /** Existing template gallery row ids on the live product. Lets us compute "remove N" from `gallery.keepIds`. */
  galleryImageIds?: number[];
}

export interface ProposedChangesDiffProps {
  proposedChanges: Record<string, unknown>;
  currentValues?: Record<string, unknown>;
  productContext?: ProductContext;
}

const FIELD_LABELS: Record<string, string> = {
  // Profile / bank account
  name: "Name",
  companyName: "Company name",
  crNumber: "CR number",
  taxNumber: "Tax number",
  contactName: "Contact name",
  street: "Street address",
  street2: "Street address line 2",
  city: "City",
  zip: "ZIP / postal code",
  countryId: "Country",
  stateId: "State / province",
  profileImage: "Profile image",
  iban: "IBAN",
  swift: "SWIFT",
  accountHolder: "Account holder",
  bankName: "Bank name",
  isDefault: "Default account",
  // Product
  description: "Description",
  countryOfOriginId: "Country of origin",
  categoryId: "Category",
  image: "Main image",
  gallery: "Gallery",
  variants: "Variants",
  newVariants: "New variants",
  discounts: "Variant discounts",
  archiveVariantIds: "Archived variants",
  compatibleWarrantyIds: "Compatible warranties",
  active: "Active",
  lifecycleStatus: "Lifecycle status",
};

const QAR = new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

function formatScalar(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function variantLabel(id: number, ctx?: ProductContext): string {
  const v = ctx?.variantsById.get(id);
  if (!v) return `Variant #${id}`;
  return v.sku ? `${v.sku} — ${v.displayName}` : v.displayName;
}

/**
 * Friendly summary for the `image` (main image) key.
 *
 * Sellers send either a base64 string (raw upload) or a GCS object key after
 * pre-upload. Either way we don't render the image itself here; we just say
 * "new main image" since the seller can preview it on the product page.
 */
function imageSummary(value: unknown): string {
  if (typeof value !== "string" || value.length === 0) return "—";
  return "New main image uploaded";
}

interface GalleryPatch { keepIds?: number[]; add?: string[] }

function gallerySummary(value: unknown, ctx?: ProductContext): string {
  if (!value || typeof value !== "object") return "—";
  const g = value as GalleryPatch;
  const adds = g.add?.length ?? 0;
  const keptCount = g.keepIds?.length ?? 0;
  const existingCount = ctx?.galleryImageIds?.length ?? null;
  const removed = existingCount != null ? Math.max(0, existingCount - keptCount) : null;
  const parts: string[] = [];
  if (adds > 0) parts.push(`+${adds} new image${adds === 1 ? "" : "s"}`);
  if (removed != null && removed > 0) parts.push(`−${removed} removed`);
  if (parts.length === 0) {
    return existingCount != null && keptCount === existingCount
      ? "No gallery changes"
      : `Keeps ${keptCount} existing image${keptCount === 1 ? "" : "s"}`;
  }
  return parts.join(", ");
}

interface VariantPatch { id: number; quantity?: number; sellerPrice?: number; image?: string; priceExtra?: number }

function VariantPatchTable({ rows, ctx }: { rows: VariantPatch[]; ctx?: ProductContext }) {
  if (rows.length === 0) return <Text variant="bodySm" tone="muted">—</Text>;

  // Only render columns that any row in this proposal actually changes — the
  // admin/seller doesn't need an empty "Stock" column when the only edit was
  // a price change.
  const showPrice = rows.some((r) => r.sellerPrice !== undefined);
  const showStock = rows.some((r) => r.quantity !== undefined);
  const showImage = rows.some((r) => typeof r.image === "string" && r.image.length > 0);

  const cols = ["minmax(0,2fr)"];
  if (showPrice) cols.push("minmax(0,1fr)");
  if (showStock) cols.push("minmax(0,1fr)");
  if (showImage) cols.push("minmax(0,1fr)");
  const gridTemplate = { gridTemplateColumns: cols.join(" ") };

  return (
    <div className="overflow-hidden rounded-xl border border-borderDefault bg-bgWhite">
      <div className="grid gap-3 bg-bgLight px-3 py-2" style={gridTemplate}>
        <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Variant</Text>
        {showPrice && <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Price (QAR)</Text>}
        {showStock && <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Stock</Text>}
        {showImage && <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Image</Text>}
      </div>
      {rows.map((r) => {
        const v = ctx?.variantsById.get(r.id);
        return (
          <div
            key={r.id}
            className="grid items-center gap-3 border-t border-borderDefault px-3 py-2"
            style={gridTemplate}
          >
            <span className="min-w-0 truncate" title={variantLabel(r.id, ctx)}>
              <Text variant="bodySmBold" tone="dark" inline>{variantLabel(r.id, ctx)}</Text>
            </span>
            {showPrice && (
              <span>
                {r.sellerPrice !== undefined
                  ? renderDelta(v?.currentPrice ?? null, r.sellerPrice, (n) => QAR.format(n))
                  : <Text variant="bodySm" tone="muted" inline>—</Text>}
              </span>
            )}
            {showStock && (
              <span>
                {r.quantity !== undefined
                  ? renderDelta(v?.currentStock ?? null, r.quantity, (n) => String(n))
                  : <Text variant="bodySm" tone="muted" inline>—</Text>}
              </span>
            )}
            {showImage && (
              <span>
                {r.image ? (
                  <Text variant="bodySm" tone="dark" inline>New image</Text>
                ) : (
                  <Text variant="bodySm" tone="muted" inline>—</Text>
                )}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface NewVariantAttrValue { attributeId: number; valueId?: number; newValueName?: string }
interface NewVariantPatch {
  attributeValues?: NewVariantAttrValue[];
  sku?: string;
  sellerPrice?: number;
  quantity?: number;
  image?: string;
  discountPercentage?: number;
}

function newVariantAxisLabel(av: NewVariantAttrValue, ctx?: ProductContext): string {
  if (av.newValueName != null && av.newValueName.trim() !== "") {
    return `${av.newValueName.trim()} (new)`;
  }
  if (av.valueId != null) {
    return ctx?.attributeValueNameById?.get(av.valueId) ?? `#${av.valueId}`;
  }
  return "—";
}

function NewVariantsTable({ rows, ctx }: { rows: NewVariantPatch[]; ctx?: ProductContext }) {
  if (rows.length === 0) return <Text variant="bodySm" tone="muted">—</Text>;
  return (
    <div className="overflow-hidden rounded-xl border border-borderDefault bg-bgWhite">
      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3 bg-bgLight px-3 py-2">
        <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Variant</Text>
        <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">SKU</Text>
        <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Price (QAR)</Text>
        <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Stock</Text>
      </div>
      {rows.map((r, i) => {
        const axisLabel =
          (r.attributeValues ?? [])
            .map((av) => newVariantAxisLabel(av, ctx))
            .filter((s) => s !== "—")
            .join(" / ") || "New variant";
        const extras: string[] = [];
        if (typeof r.discountPercentage === "number" && r.discountPercentage > 0) {
          extras.push(`${r.discountPercentage}% discount`);
        }
        if (typeof r.image === "string" && r.image.length > 0) extras.push("image attached");
        return (
          <div
            key={i}
            className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] items-start gap-3 border-t border-borderDefault px-3 py-2"
          >
            <span className="flex min-w-0 flex-col">
              <span className="min-w-0 truncate" title={axisLabel}>
                <Text variant="bodySmBold" tone="dark" inline>{axisLabel}</Text>
              </span>
              {extras.length > 0 && (
                <Text variant="bodyXs" tone="muted">{extras.join(" · ")}</Text>
              )}
            </span>
            <Text variant="bodySm" tone="dark" inline>{r.sku || "—"}</Text>
            <Text variant="bodySm" tone="dark" inline>
              {typeof r.sellerPrice === "number" ? QAR.format(r.sellerPrice) : "—"}
            </Text>
            <Text variant="bodySm" tone="dark" inline>
              {typeof r.quantity === "number" ? String(r.quantity) : "—"}
            </Text>
          </div>
        );
      })}
    </div>
  );
}

function renderDelta(current: number | null | undefined, proposed: number, format: (n: number) => string) {
  if (current == null || current === proposed) {
    return <Text variant="bodySm" tone="dark" inline>{format(proposed)}</Text>;
  }
  return (
    <span className="inline-flex flex-col">
      <Text variant="bodySm" tone="dark" inline>{format(proposed)}</Text>
      <Text variant="bodyXs" tone="muted" inline className="line-through">
        {format(current)}
      </Text>
    </span>
  );
}

interface DiscountPatch { variantId: number; discountPercentage: number }

function DiscountPatchTable({ rows, ctx }: { rows: DiscountPatch[]; ctx?: ProductContext }) {
  if (rows.length === 0) return <Text variant="bodySm" tone="muted">—</Text>;
  return (
    <div className="overflow-hidden rounded-xl border border-borderDefault bg-bgWhite">
      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-3 bg-bgLight px-3 py-2">
        <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Variant</Text>
        <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Discount %</Text>
      </div>
      {rows.map((r) => {
        const v = ctx?.variantsById.get(r.variantId);
        const next = r.discountPercentage > 0 ? `${r.discountPercentage}%` : "—";
        return (
          <div
            key={r.variantId}
            className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] items-center gap-3 border-t border-borderDefault px-3 py-2"
          >
            <span className="min-w-0 truncate" title={variantLabel(r.variantId, ctx)}>
              <Text variant="bodySmBold" tone="dark" inline>{variantLabel(r.variantId, ctx)}</Text>
            </span>
            <span>
              {v?.currentDiscount != null && v.currentDiscount !== r.discountPercentage ? (
                <span className="inline-flex flex-col">
                  <Text variant="bodySm" tone="dark" inline>{next}</Text>
                  <Text variant="bodyXs" tone="muted" inline className="line-through">
                    {v.currentDiscount > 0 ? `${v.currentDiscount}%` : "—"}
                  </Text>
                </span>
              ) : (
                <Text variant="bodySm" tone="dark" inline>{next}</Text>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ArchivedList({ ids, ctx }: { ids: number[]; ctx?: ProductContext }) {
  if (ids.length === 0) return <Text variant="bodySm" tone="muted">—</Text>;
  return (
    <ul className="flex flex-col gap-1">
      {ids.map((id) => (
        <li key={id}>
          <Text variant="bodySm" tone="dark">• {variantLabel(id, ctx)}</Text>
        </li>
      ))}
    </ul>
  );
}

function IdList({ ids, label }: { ids: number[]; label: string }) {
  if (ids.length === 0) return <Text variant="bodySm" tone="muted">—</Text>;
  return (
    <Text variant="bodySm" tone="dark">
      {label}: {ids.map((id) => `#${id}`).join(", ")}
    </Text>
  );
}

function renderProposed(
  field: string,
  value: unknown,
  ctx?: ProductContext,
): { node: React.ReactNode; isComplex: boolean } {
  switch (field) {
    case "image":
      return { node: <Text variant="bodySm" tone="dark">{imageSummary(value)}</Text>, isComplex: false };
    case "gallery":
      return { node: <Text variant="bodySm" tone="dark">{gallerySummary(value, ctx)}</Text>, isComplex: false };
    case "variants":
      return {
        node: <VariantPatchTable rows={(value as VariantPatch[]) ?? []} ctx={ctx} />,
        isComplex: true,
      };
    case "newVariants":
      return {
        node: <NewVariantsTable rows={(value as NewVariantPatch[]) ?? []} ctx={ctx} />,
        isComplex: true,
      };
    case "discounts":
      return {
        node: <DiscountPatchTable rows={(value as DiscountPatch[]) ?? []} ctx={ctx} />,
        isComplex: true,
      };
    case "archiveVariantIds":
      return {
        node: <ArchivedList ids={(value as number[]) ?? []} ctx={ctx} />,
        isComplex: true,
      };
    case "compatibleWarrantyIds":
      return {
        node: <IdList ids={(value as number[]) ?? []} label="Warranty SKUs" />,
        isComplex: false,
      };
    case "countryOfOriginId":
    case "countryId": {
      const id = typeof value === "number" ? value : Number(value);
      const name = ctx?.countryNameById?.get(id);
      return {
        node: <Text variant="bodySm" tone="dark">{name ?? (Number.isFinite(id) ? `#${id}` : "—")}</Text>,
        isComplex: false,
      };
    }
    case "categoryId": {
      const id = typeof value === "number" ? value : Number(value);
      const name = ctx?.categoryNameById?.get(id);
      return {
        node: <Text variant="bodySm" tone="dark">{name ?? (Number.isFinite(id) ? `#${id}` : "—")}</Text>,
        isComplex: false,
      };
    }
    default:
      return {
        node: <Text variant="bodySm" tone="dark">{formatScalar(value)}</Text>,
        isComplex: false,
      };
  }
}

function renderCurrent(
  field: string,
  value: unknown,
  ctx?: ProductContext,
): React.ReactNode {
  // For complex product fields the "current" side doesn't have a clean inline
  // shape on the request, so we skip the side-by-side and just rely on the
  // delta indicators inside the proposed table (strikethrough current price /
  // stock / discount in each row).
  if (
    field === "variants" ||
    field === "newVariants" ||
    field === "discounts" ||
    field === "archiveVariantIds"
  ) {
    return null;
  }
  if (field === "countryOfOriginId" || field === "countryId") {
    const id = typeof value === "number" ? value : Number(value);
    const name = ctx?.countryNameById?.get(id);
    return <Text variant="bodySm" tone="muted">{name ?? (Number.isFinite(id) ? `#${id}` : "—")}</Text>;
  }
  if (field === "categoryId") {
    const id = typeof value === "number" ? value : Number(value);
    const name = ctx?.categoryNameById?.get(id);
    return <Text variant="bodySm" tone="muted">{name ?? (Number.isFinite(id) ? `#${id}` : "—")}</Text>;
  }
  if (field === "image") {
    return <Text variant="bodySm" tone="muted">Live image</Text>;
  }
  if (field === "gallery") {
    const count = ctx?.galleryImageIds?.length ?? 0;
    return <Text variant="bodySm" tone="muted">{count} image{count === 1 ? "" : "s"}</Text>;
  }
  return <Text variant="bodySm" tone="muted">{formatScalar(value)}</Text>;
}

export function ProposedChangesDiff({
  proposedChanges,
  currentValues,
  productContext,
}: ProposedChangesDiffProps) {
  const fields = Object.keys(proposedChanges);
  const showCurrent = !!currentValues;

  if (fields.length === 0) {
    return (
      <Text variant="bodySm" tone="muted">
        No changes proposed.
      </Text>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {fields.map((field) => {
        const proposed = proposedChanges[field];
        const current = currentValues?.[field];
        const changed = !showCurrent || proposed !== current;
        const { node: proposedNode, isComplex } = renderProposed(field, proposed, productContext);
        const currentNode = renderCurrent(field, current, productContext);
        const sideBySide = showCurrent && !isComplex && currentNode !== null;
        return (
          <div
            key={field}
            className={clsx(
              "rounded-2xl border border-borderDefault bg-bgWhite px-4 py-3",
              changed && showCurrent && "border-accentAmber/40 bg-accentYellowTint/20",
            )}
          >
            <div className="px-1.5 mb-2">
              <Text variant="bodySmBold" inline tone="body">
                {FIELD_LABELS[field] ?? field}
              </Text>
            </div>
            <div
              className={clsx(
                "grid gap-3",
                sideBySide ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1",
              )}
            >
              {sideBySide && (
                <ValueBox label="Current">{currentNode}</ValueBox>
              )}
              <ValueBox label="Proposed">{proposedNode}</ValueBox>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ValueBox({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">
        {label}
      </Text>
      <div className="flex min-h-[44px] flex-col justify-center rounded-xl border border-borderDefault bg-bgLight px-3 py-2">
        {children}
      </div>
    </div>
  );
}
